import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const TYPES = [["buyer","Buyer"],["seller","Seller"],["both","Both"]];

/**
 * Edit modal for a Client. Deliberately mirrors `LeadEdit` field-for-
 * field (minus the pipeline-stage + TCPA consent block, which don't
 * apply to a client) so an agent moving a person between pipelines
 * doesn't re-learn a new form.
 *
 * `full_name` is composed server-side from `first_name + last_name`, so
 * we never send it here — the split fields are the single source of
 * truth once an agent touches this modal.
 */
export default function ClientEdit({ client, onClose, onSaved }) {
  const [f, setF] = useState({
    first_name: client.first_name || "",
    last_name: client.last_name || "",
    email: client.email || "",
    phone: client.phone || "",
    address: client.address || "",
    source: client.source || "",
    client_type: client.client_type || "buyer",
    property_interest: client.property_interest || "",
    budget_min: client.budget_min ?? "",
    budget_max: client.budget_max ?? "",
    notes: client.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const composed = [f.first_name, f.last_name].map(s => (s || "").trim()).filter(Boolean).join(" ");
    if (!composed && !client.full_name) return toast.error("First and last name are required");
    if (!(f.email || "").trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      const body = {
        ...f,
        budget_min: f.budget_min === "" ? null : Number(f.budget_min),
        budget_max: f.budget_max === "" ? null : Number(f.budget_max),
        // Coerce blanks to null so the PATCH clears the column instead
        // of storing "".
        phone: f.phone || null,
        address: f.address || null,
        source: f.source || null,
        property_interest: f.property_interest || null,
        notes: f.notes || null,
      };
      await api.patch(`/clients/${client.id}`, body);
      toast.success("Client updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-label="Edit client" data-testid="client-edit-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Edit client</h2>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="client-edit-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 pt-0 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="First name" required><input value={f.first_name} onChange={e=>setF({...f, first_name: e.target.value})} className="input" data-testid="client-edit-first-name" /></F>
            <F label="Last name" required><input value={f.last_name} onChange={e=>setF({...f, last_name: e.target.value})} className="input" data-testid="client-edit-last-name" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Email" required><input type="email" value={f.email} onChange={e=>setF({...f, email: e.target.value})} className="input" data-testid="client-edit-email" /></F>
            <F label="Phone"><input value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} className="input" data-testid="client-edit-phone" /></F>
          </div>
          <F label="Address"><input value={f.address} onChange={e=>setF({...f, address: e.target.value})} placeholder="Home / mailing address" className="input" data-testid="client-edit-address" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Source"><input value={f.source} onChange={e=>setF({...f, source: e.target.value})} placeholder="Zillow, referral, open house…" className="input" data-testid="client-edit-source" /></F>
            <F label="Type">
              <select value={f.client_type} onChange={e=>setF({...f, client_type: e.target.value})} className="input" data-testid="client-edit-type">
                {TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </F>
          </div>
          <F label="Property interest"><textarea value={f.property_interest} onChange={e=>setF({...f, property_interest: e.target.value})} rows={2} placeholder="Luxury single-family in Arcadia; pool required" className="input" data-testid="client-edit-property-interest" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Price min"><input type="number" value={f.budget_min} onChange={e=>setF({...f, budget_min: e.target.value})} className="input" data-testid="client-edit-budget-min" /></F>
            <F label="Price max"><input type="number" value={f.budget_max} onChange={e=>setF({...f, budget_max: e.target.value})} className="input" data-testid="client-edit-budget-max" /></F>
          </div>
          <F label="Notes"><textarea value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} className="input min-h-[80px]" data-testid="client-edit-notes" /></F>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="client-edit-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
